'use client';

import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { settingAPI, productAPI } from '@/lib/api';
import HotProducts from './HotProducts';
import LatestArticles from './LatestArticles';

interface MenuItem {
  title: string;
  url: string;
}

/**
 * 全局右侧导航栏组件
 * 在所有非首页页面显示，包含四个导航标题：
 * 1. Product center
 * 2. About Us
 * 3. Hot Product
 * 4. Latest Articles
 * 
 * 采用现代化的设计风格，参考用户提供的设计规范
 */
export default function GlobalNavSidebar() {
  const pathname = usePathname();
  const [aboutMenuItems, setAboutMenuItems] = React.useState<MenuItem[]>([
    { title: 'About Us', url: '/about-us' },
    { title: 'Contact Us', url: '/contact-us' },
  ]);
  const [hotProductsLimit, setHotProductsLimit] = React.useState(4);
  const [latestArticlesLimit, setLatestArticlesLimit] = React.useState(4);
  const [productCategories, setProductCategories] = React.useState<any[]>([]);

  // 从API加载配置
  React.useEffect(() => {
    const loadSettings = async () => {
      try {
        const settings = await settingAPI.getAll();
        
        // 解析关于我们菜单
        if (settings.sidebar_about_menu) {
          try {
            const menu = typeof settings.sidebar_about_menu === 'string'
              ? JSON.parse(settings.sidebar_about_menu)
              : settings.sidebar_about_menu;
            if (Array.isArray(menu) && menu.length > 0) {
              setAboutMenuItems(menu);
            }
          } catch (e) {
            console.error('Failed to parse sidebar_about_menu:', e);
          }
        }
        
        // 设置显示数量
        if (settings.sidebar_hot_products_limit) {
          setHotProductsLimit(Number(settings.sidebar_hot_products_limit) || 4);
        }
        if (settings.sidebar_latest_articles_limit) {
          setLatestArticlesLimit(Number(settings.sidebar_latest_articles_limit) || 4);
        }
      } catch (error) {
        console.error('Failed to load sidebar settings:', error);
      }
    };
    
    loadSettings();
  }, []);

  // 从API加载产品分类
  React.useEffect(() => {
    const loadCategories = async () => {
      try {
        const response = await productAPI.getCategories();
        const categories = response.data || [];
        // 只获取顶级分类（没有parentId的）
        const topLevelCategories = categories.filter((cat: any) => !cat.parentId);
        setProductCategories(topLevelCategories);
      } catch (error) {
        console.error('Failed to load product categories:', error);
      }
    };
    
    loadCategories();
  }, []);

  // 检查链接是否激活
  const isActive = (url: string) => {
    if (!url) return false;
    const checkUrl = url.split('?')[0];
    const checkPathname = pathname.split('?')[0];
    return checkPathname === checkUrl || checkPathname?.startsWith(checkUrl + '/');
  };

  return (
    <aside className="global-sidebar">
      {/* 产品中心 */}
      <div className="sidebar-section">
        <h3 className="sidebar-title">
          <svg className="sidebar-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2 2H6V6H2V2Z" fill="currentColor"/>
            <path d="M10 2H14V6H10V2Z" fill="currentColor"/>
            <path d="M2 10H6V14H2V10Z" fill="currentColor"/>
            <path d="M10 10H14V14H10V10Z" fill="currentColor"/>
          </svg>
          <span>Product center</span>
        </h3>
        <ul className="sidebar-menu">
          {productCategories.map((category) => (
            <li key={category.id}>
              <Link 
                href={`/products?category=${category.slug}`}
                className={`sidebar-link ${isActive(`/products?category=${category.slug}`) ? 'active' : ''}`}
              >
                {category.name}
              </Link>
            </li>
          ))}
        </ul>
      </div>

      {/* 关于我们 */}
      <div className="sidebar-section">
        <h3 className="sidebar-title">
          <svg className="sidebar-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2 2H14V14H2V2Z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <path d="M5 6H11M5 9H11M5 12H9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          <span>About Us</span>
        </h3>
        <ul className="sidebar-menu">
          {aboutMenuItems.map((item, index) => (
            <li key={index}>
              <Link 
                href={item.url} 
                className={`sidebar-link ${isActive(item.url) ? 'active' : ''}`}
              >
                {item.title}
              </Link>
            </li>
          ))}
        </ul>
      </div>

      {/* 热门产品 */}
      <div className="sidebar-section">
        <h3 className="sidebar-title">
          <svg className="sidebar-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8 1L10.09 5.26L15 6.11L11.5 9.26L12.18 14.18L8 11.77L3.82 14.18L4.5 9.26L1 6.11L5.91 5.26L8 1Z" fill="currentColor"/>
          </svg>
          <span>Hot Product</span>
        </h3>
        <HotProducts limit={hotProductsLimit} />
      </div>

      {/* 最新文章 */}
      <div className="sidebar-section">
        <h3 className="sidebar-title">
          <svg className="sidebar-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2 2H14V14H2V2Z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <path d="M5 5H11M5 8H11M5 11H9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          <span>Latest Articles</span>
        </h3>
        <LatestArticles limit={latestArticlesLimit} />
      </div>
    </aside>
  );
}

